/**
 * One copilot drawer, one socket, for the whole recruiter app.
 *
 * Every entry point — the job page header, an applicant row, the Results
 * header, the Navbar — asks this provider to open, rather than owning a
 * drawer of its own. Separate drawers would stack, and separate sockets would
 * supersede each other with 4409.
 *
 * Mounted around the recruiter route set, outside its Suspense boundary, so
 * the drawer survives navigation and lazy route loads.
 */

import React, {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { handleInvalidToken } from "../utils/tokenUtils";
import { listCopilotSessions } from "./api";
import { NEW_SESSION, RESUME_WINDOW_MS } from "./constants";
import { latestSessionFor, subjectFromSession, subjectKeyOf } from "./subjects";
import "./copilot.css";

// The MUI drawer shell (and, inside it, the lazy panel) is its own chunk so
// @mui/material stays out of the entry bundle. It starts loading when this
// provider mounts, so it is normally ready before the first open.
const CopilotDrawer = lazy(() => import("./CopilotDrawer"));

/**
 * Holds only stable callbacks, so the value never changes and no consumer
 * re-renders because the drawer opened. That matters: the job page is a large
 * table, and it consumes this for its row action.
 */
const CopilotContext = createContext(null);

/** null outside a provider — consumers render nothing rather than throw. */
export const useCopilot = () => useContext(CopilotContext);

export function CopilotProvider({ children }) {
  const [open, setOpen] = useState(false);
  /** Drawer width: the reference's expand control. Survives subject switches. */
  const [expanded, setExpanded] = useState(false);
  /**
   * What the panel connects to. `generation` changes on every explicit
   * switch so the panel remounts — that resets its local UI and makes "New"
   * start a fresh session even when the id is already "new".
   */
  const [target, setTarget] = useState({
    subject: null,
    sessionId: NEW_SESSION,
    generation: 0,
    /** Looking up the subject's latest chat; the panel waits for it. */
    resolving: false,
  });
  /**
   * The subject of the page the user is on, for the global launcher. A ref,
   * not state: it is only read at click time, and state would re-render every
   * consumer on each navigation.
   */
  const pageSubjectRef = useRef(null);
  /** Same idea for pages with no record behind them (Team Performance + period). */
  const pageContextRef = useRef(null);
  /** Rows ticked in the table on screen, as [{ kind, id }], kept live. */
  const selectionRef = useRef(null);

  const openRef = useRef(false);
  /** The live session id, so reopening on the same subject continues it. */
  const liveSessionRef = useRef({ key: null, id: null });

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const openCopilot = useCallback((subject = null) => {
    const key = subjectKeyOf(subject);
    setTarget((current) => {
      const sameSubject = subjectKeyOf(current.subject) === key;
      // Already showing this subject — just bring the drawer forward.
      if (sameSubject && openRef.current) return current;
      const live = liveSessionRef.current;
      const liveId = sameSubject && live.key === key && live.id ? live.id : null;
      return {
        subject,
        pageContext: pageContextRef.current,
        sessionId: liveId ?? NEW_SESSION,
        generation: current.generation + 1,
        // Nothing in memory (a refresh, or another subject since): ask the
        // server for this subject's recent chat before connecting.
        resolving: !liveId && key !== "none",
      };
    });
    setOpen(true);
  }, []);

  // The lookup behind `resolving`. Keyed on generation, so a result for a
  // subject the user has already left is dropped. Any failure starts fresh:
  // the lookup must never stand between the user and a chat.
  useEffect(() => {
    if (!target.resolving) return undefined;
    const { subject, generation } = target;
    let cancelled = false;
    listCopilotSessions(25)
      .then((data) =>
        latestSessionFor(data?.sessions, subject, { windowMs: RESUME_WINDOW_MS })
      )
      .catch(() => null)
      .then((sessionId) => {
        if (cancelled) return;
        setTarget((current) =>
          current.generation === generation
            ? { ...current, sessionId: sessionId ?? NEW_SESSION, resolving: false }
            : current
        );
      });
    return () => {
      cancelled = true;
    };
    // `target` changes identity on resolve; generation is what identifies it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.resolving, target.generation]);

  const closeCopilot = useCallback(() => setOpen(false), []);

  const toggleExpanded = useCallback(() => setExpanded((value) => !value), []);

  /**
   * A genuine credential failure (4401, or `revoked` before it). The app's
   * handler opens an antd "Session expired" modal at z-index 1000, which the
   * drawer would cover — so the drawer closes first.
   */
  const handleUnauthorized = useCallback(() => {
    setOpen(false);
    handleInvalidToken();
  }, []);

  /** Resume with the session's OWN subject, never the page's. */
  const resumeSession = useCallback((session) => {
    if (!session?.id) return;
    setTarget((current) => ({
      subject: subjectFromSession(session),
      pageContext: pageContextRef.current,
      sessionId: String(session.id),
      generation: current.generation + 1,
    }));
  }, []);

  const newSession = useCallback(() => {
    liveSessionRef.current = { key: null, id: null };
    setTarget((current) => ({
      subject: current.subject,
      pageContext: pageContextRef.current,
      sessionId: NEW_SESSION,
      generation: current.generation + 1,
    }));
  }, []);

  /** Last mount wins; cleanup clears only if it is still the registered one. */
  const registerPageSubject = useCallback((subject) => {
    const key = subjectKeyOf(subject);
    pageSubjectRef.current = subject;
    return () => {
      if (subjectKeyOf(pageSubjectRef.current) === key) {
        pageSubjectRef.current = null;
      }
    };
  }, []);

  const getPageSubject = useCallback(() => pageSubjectRef.current, []);

  /** Last mount wins; cleanup clears only if it is still the registered one. */
  const registerPageContext = useCallback((context) => {
    pageContextRef.current = context;
    return () => {
      if (pageContextRef.current === context) pageContextRef.current = null;
    };
  }, []);

  /** Last mount wins; cleanup clears only if it is still the registered one. */
  const registerSelection = useCallback((selection) => {
    selectionRef.current = selection;
    return () => {
      if (selectionRef.current === selection) selectionRef.current = null;
    };
  }, []);

  /**
   * What the user is looking at, read when a message is SENT (not when Hairo
   * opened): the route and its query string, the page's registered context
   * and the ticked rows. A hint only - the server checks every id it names.
   */
  const getPageView = useCallback(
    () => ({
      path: window.location.pathname,
      query: window.location.search,
      ...(pageContextRef.current || {}),
      selected: selectionRef.current || [],
    }),
    []
  );

  const rememberSession = useCallback((subject, sessionId) => {
    if (!sessionId || sessionId === NEW_SESSION) return;
    liveSessionRef.current = { key: subjectKeyOf(subject), id: String(sessionId) };
  }, []);

  /**
   * Unsent composer text per subject. Closing the drawer unmounts the panel
   * and its composer; this keeps a half-typed question for the reopen. Memory
   * only: a refresh, or leaving the recruiter pages, clears it.
   */
  const draftsRef = useRef(new Map());
  const getDraft = useCallback(
    (subject) => draftsRef.current.get(subjectKeyOf(subject)) ?? "",
    []
  );
  const saveDraft = useCallback((subject, text) => {
    const key = subjectKeyOf(subject);
    if (text) draftsRef.current.set(key, text);
    else draftsRef.current.delete(key);
  }, []);

  // Every member is a stable callback, so this is computed once.
  const value = useMemo(
    () => ({
      openCopilot,
      closeCopilot,
      resumeSession,
      newSession,
      registerPageSubject,
      getPageSubject,
      registerPageContext,
      registerSelection,
      getPageView,
    }),
    [
      openCopilot,
      closeCopilot,
      resumeSession,
      newSession,
      registerPageSubject,
      getPageSubject,
      registerPageContext,
      registerSelection,
      getPageView,
    ]
  );

  return (
    <CopilotContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        <CopilotDrawer
          open={open}
          expanded={expanded}
          target={target}
          onClose={closeCopilot}
          onToggleExpand={toggleExpanded}
          onUnauthorized={handleUnauthorized}
          onResume={resumeSession}
          onNew={newSession}
          onSessionReady={rememberSession}
          getDraft={getDraft}
          onDraftChange={saveDraft}
        />
      </Suspense>
    </CopilotContext.Provider>
  );
}

export default CopilotProvider;
